import {
  ExtrudeFeatureEditDefinitionFactory,
  LineHorizontalConstraint,
  LineMaterialFactory,
  LineMaterialState,
  LineVerticalConstraint,
  PointsMaterialFactory,
  SketchArcObject,
  SketchCircleObject,
  SketchCollectionObject,
  SketchLineObject,
  SketchPointKind,
  SketchPointObject,
  ViewMode,
  type Viewer,
} from '@puppycad/cad-core';
import * as THREE from 'three/webgpu';

/**
 * 调试基准面名称枚举。
 */
enum DebugDatumPlaneName {
  /** 前视基准面。 */
  Front = '前视基准面',
}

/**
 * 调试正方形预设值枚举。
 */
const DebugSquarePresetValue = {
  /** 正方形边长。 */
  SquareSide: 50,
  /** 控制台快速调试正方形边长。 */
  QuickSquareSide: 5,
  /** 两个调试草图之间的间距。 */
  BoxGap: 30,
} as const;

/** 组合草图矩形宽度预设。 */
const DEBUG_COMPOSITE_RECTANGLE_WIDTH = 10;
/** 组合草图矩形高度预设。 */
const DEBUG_COMPOSITE_RECTANGLE_HEIGHT = 10;
/** 组合草图圆半径预设。 */
const DEBUG_COMPOSITE_CIRCLE_RADIUS = 5;
/** 组合草图轮廓间距预设。 */
const DEBUG_COMPOSITE_SHAPE_GAP = 10;
/** 组合草图拉伸深度预设。 */
const DEBUG_COMPOSITE_EXTRUDE_DEPTH = 10;

/** 调试拉伸深度预设。 */
const DEBUG_EXTRUDE_DEPTH = DebugSquarePresetValue.SquareSide;
/** 控制台快速调试拉伸深度预设。 */
const DEBUG_QUICK_EXTRUDE_DEPTH = DebugSquarePresetValue.QuickSquareSide;

//------ DebugTool 场景测试盒 开始------
/**
 * 场景测试盒预设枚举。
 */
enum DebugSceneTestBoxPreset {
  /** 场景测试盒边长。 */
  Size = 2,
  /** 场景测试盒颜色。 */
  Color = 0x22c55e,
  /** 场景测试盒渲染顺序。 */
  RenderOrder = 997,
}

/**
 * 场景测试盒名称枚举。
 */
enum DebugSceneTestBoxName {
  /** 主场景 2x2x2 测试盒。 */
  SceneTestBox = 'DebugTool_场景测试2x2x2盒子',
}
//------ DebugTool 场景测试盒 结束------

//------ DebugTool 场景测试线 开始------
/**
 * 场景测试线预设枚举。
 */
enum DebugSceneTestLinePreset {
  /** 场景测试线颜色。 */
  Color = 0x38bdf8,
  /** 场景测试线渲染顺序。 */
  RenderOrder = 997,
}

/**
 * 场景测试线名称枚举。
 */
enum DebugSceneTestLineName {
  /** 主场景测试线。 */
  SceneTestLine = 'DebugTool_场景测试线',
}

/** 场景测试线默认起点。 */
const DEBUG_SCENE_TEST_LINE_DEFAULT_START = new THREE.Vector3(-50, 0, 0);
/** 场景测试线默认终点。 */
const DEBUG_SCENE_TEST_LINE_DEFAULT_END = new THREE.Vector3(50, 0, 0);
//------ DebugTool 场景测试线 结束------

//------ DebugTool 线弧 U 形轮廓调试 开始------
/**
 * 线弧 U 形轮廓调试预设枚举。
 */
enum DebugLineArcUProfilePreset {
  /** 外侧圆弧半径。 */
  OuterRadius = 30,
  /** 内侧圆弧半径。 */
  InnerRadius = 12,
  /** 内侧圆弧圆心 Y 坐标。 */
  InnerCenterY = 24,
  /** 左右竖直边顶部 Y 坐标。 */
  TopY = 60,
  /** 圆弧采样段数。 */
  ArcSegments = 64,
}

/**
 * 线弧 U 形轮廓调试对象名称枚举。
 */
enum DebugLineArcUProfileName {
  /** 线弧 U 形闭合集合对象。 */
  Collection = 'DebugTool_线弧U形闭合轮廓',
}
//------ DebugTool 线弧 U 形轮廓调试 结束------

//------ DebugTool UUID定位包围盒 开始------
/**
 * UUID 定位包围盒预设枚举。
 */
enum DebugFocusBoundingBoxPreset {
  /** 定位包围盒颜色。 */
  Color = 0xf97316,
  /** 定位包围盒渲染顺序。 */
  RenderOrder = 998,
}

/**
 * UUID 定位包围盒名称枚举。
 */
enum DebugFocusBoundingBoxName {
  /** UUID 定位包围盒。 */
  FocusedObjectBounds = 'DebugTool_UUID定位对象包围盒',
}
//------ DebugTool UUID定位包围盒 结束------

//------ DebugTool 控制台挂载 开始------
/**
 * 调试矩形拉伸输入参数。
 */
interface DebugSquareExtrudeInput {
  /** 正方形边长。 */
  squareSide: number;
  /** 拉伸深度。 */
  extrudeDepth: number;
}

//------ DebugTool 矩形圆形联合拉伸 开始------
/**
 * 调试“矩形 + 圆”联合拉伸输入参数。
 */
interface DebugRectangleCircleExtrudeInput {
  /** 矩形宽度。 */
  rectangleWidth: number;
  /** 矩形高度。 */
  rectangleHeight: number;
  /** 圆半径。 */
  circleRadius: number;
  /** 两个轮廓之间的最小间距。 */
  shapeGap: number;
  /** 拉伸深度。 */
  extrudeDepth: number;
}
//------ DebugTool 矩形圆形联合拉伸 结束------

/**
 * 控制台调试工具对象。
 */
export interface DebugToolWindowFacade {
  /**
   * 获取当前绑定的 Viewer。
   *
   * @returns 当前 Viewer 实例。
   */
  getViewer: () => Viewer;
  /**
   * 创建默认 50x50 正方形并执行默认深度拉伸。
   *
   * @returns 拉伸提交结果。
   */
  createFrontPlaneSquareExtrude: () => Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>>;
  /**
   * 快速创建 5x5 正方形并执行深度为 5 的拉伸。
   *
   * @returns 拉伸提交结果。
   */
  createFrontPlaneFiveSquareExtrude: () => Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>>;
  /**
   * 按指定尺寸创建正方形并执行拉伸。
   *
   * @param squareSide 正方形边长。
   * @param extrudeDepth 拉伸深度；缺省时与边长一致。
   * @returns 拉伸提交结果。
   */
  createFrontPlaneSquareExtrudeBySize: (
    squareSide: number,
    extrudeDepth?: number,
  ) => Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>>;
  /**
   * 创建 `10x10` 矩形与 `R5` 圆，并一次性执行深度为 `10` 的多轮廓拉伸。
   *
   * @returns 拉伸提交结果。
   */
  createFrontPlaneRectangleCircleExtrude: () => Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>>;
  /**
   * 创建两个相距固定间距的 `50x50` 正方形，并分别执行独立拉伸。
   *
   * @returns 调试建模执行结果。
   */
  createTwoDistancedSquareSketches: () => Promise<void>;
  //------ DebugTool 线弧 U 形轮廓调试 开始------
  /**
   * 在前视基准面创建线弧 U 形闭合轮廓草图，并停留在草图编辑态。
   *
   * @returns 草图创建结果。
   */
  createFrontPlaneLineArcUProfileSketch: () => Promise<DebugRectangleSketchCreationResult>;
  //------ DebugTool 线弧 U 形轮廓调试 结束------
  //------ DebugTool 场景测试盒 开始------
  /**
   * 创建一个 `2 x 2 x 2` 的场景测试盒并添加到主场景。
   *
   * @returns 已添加到场景中的测试盒 Mesh。
   */
  createSceneTestBox: () => THREE.Mesh<THREE.BoxGeometry, THREE.MeshBasicMaterial>;
  /**
   * 计算当前 Trim 候选集合的全部交点，并在每个交点创建圆形调试精灵。
   *
   * @returns 本次创建的交点调试精灵数量。
   */
  showAllTrimIntersectionBoxes: () => number;
  /**
   * 清理当前 Trim 交点调试精灵。
   */
  clearTrimIntersectionBoxes: () => void;
  //------ DebugTool 场景测试线 开始------
  /**
   * 创建一条场景测试线并添加到主场景。
   *
   * @param start 测试线起点；缺省时使用 `(-50, 0, 0)`。
   * @param end 测试线终点；缺省时使用 `(50, 0, 0)`。
   * @param color 测试线颜色；缺省时使用内置调试蓝。
   * @returns 已添加到场景中的测试线。
   */
  createSceneDebugLine: (
    start?: THREE.Vector3,
    end?: THREE.Vector3,
    color?: number,
  ) => THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  //------ DebugTool 场景测试线 结束------
  //------ DebugTool UUID定位 开始------
  /**
   * 按对象 uuid（兼容对象名）定位到模型。
   *
   * @param uuid 目标对象 uuid（兼容对象名）。
   * @returns 定位命中的对象；未命中时返回 `null`。
   */
  focusModelByUuid: (uuid: string) => THREE.Object3D | null;
  //------ DebugTool UUID定位 结束------
  //------ DebugTool 场景测试盒 结束------
}

declare global {
  interface Window {
    /** 控制台调试工具对象。 */
    DebugTool?: DebugToolWindowFacade;
  }
}
//------ DebugTool 控制台挂载 结束------

/**
 * 调试矩形草图创建结果。
 */
export interface DebugRectangleSketchCreationResult {
  /** 草图 ID。 */
  sketchId: string;
  /** 轮廓宿主对象 ID。 */
  contourOwnerId: string;
}

//------ DebugTool 矩形圆形联合拉伸 开始------
/**
 * 调试草图多轮廓创建结果。
 */
interface DebugMultiContourSketchCreationResult {
  /** 草图 ID。 */
  sketchId: string;
  /** 轮廓宿主对象 ID 列表。 */
  contourOwnerIds: string[];
}
//------ DebugTool 矩形圆形联合拉伸 结束------

/**
 * Web 客户端调试工具。
 *
 * 职责：
 * 1. 统一提供面向调试场景的高层建模入口；
 * 2. 负责调用 `Viewer` 的公开能力完成草图与拉伸链路；
 * 3. 不模拟 UI 点击，直接走底层稳定建模对象创建流程。
 *
 * 控制台快速调用说明：
 * 1. `window.DebugTool.getViewer()`：返回当前绑定的 `Viewer`，用于进一步查看调试态或手动调用底层能力；
 * 2. `window.DebugTool.createFrontPlaneSquareExtrude()`：创建默认 `50 x 50` 正方形并执行深度为 `50` 的凸台拉伸；
 * 3. `window.DebugTool.createFrontPlaneFiveSquareExtrude()`：快速创建 `5 x 5` 正方形并执行深度为 `5` 的凸台拉伸；
 * 4. `window.DebugTool.createFrontPlaneSquareExtrudeBySize(squareSide, extrudeDepth?)`：按指定边长创建正方形并执行拉伸，缺省深度与边长一致；
 * 5. `window.DebugTool.createFrontPlaneRectangleCircleExtrude()`：创建 `10 x 10` 矩形和 `R5` 圆，并以同一草图执行多轮廓拉伸；
 * 6. `window.DebugTool.createTwoDistancedSquareSketches()`：创建两个相距固定间距的 `50 x 50` 正方形，并分别生成两个独立实体。
 * 7. `window.DebugTool.createSceneTestBox()`：创建一个 `2 x 2 x 2` 的测试盒并添加到主场景。
 * 8. `window.DebugTool.showAllTrimIntersectionBoxes()`：计算当前 Trim 候选曲线全部交点，并在每个交点创建圆形调试精灵。
 * 9. `window.DebugTool.clearTrimIntersectionBoxes()`：清理当前 Trim 交点调试精灵。
 * 10. `window.DebugTool.createSceneDebugLine(start?, end?, color?)`：创建一条测试线并添加到主场景。
 * 11. `window.DebugTool.focusModelByUuid(uuid)`：按对象 uuid（兼容对象名）定位到模型。
 * 12. `window.DebugTool.createFrontPlaneLineArcUProfileSketch()`：创建线弧 U 形闭合轮廓草图，并停留在草图编辑态。
 */
export default class DebugTool {
  /**
   * 使用前视基准面创建 50x50 正方形，并执行深度为 50 的凸台拉伸。
   *
   * 设计说明：
   * 1. 先进入前视基准面法向视图，保证后续草图参考面与用户预期一致；
   * 2. 再直接构建正式矩形草图对象，而不是伪造鼠标事件；
   * 3. 草图结束后通过轮廓选择提交正式拉伸，保持与现有拉伸会话桥接链一致。
   *
   * @param viewer Viewer 实例。
   * @returns 拉伸提交结果。
   */
  public static async createFrontPlaneSquareExtrude(
    viewer: Viewer,
  ): Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>> {
    return DebugTool.createFrontPlaneSquareExtrudeByInput(viewer, {
      squareSide: DebugSquarePresetValue.SquareSide,
      extrudeDepth: DEBUG_EXTRUDE_DEPTH,
    });
  }

  //------ DebugTool 控制台 5x5 快速调试 开始------
  /**
   * 使用前视基准面快速创建 5x5 正方形，并执行深度为 5 的凸台拉伸。
   *
   * @param viewer Viewer 实例。
   * @returns 拉伸提交结果。
   */
  public static async createFrontPlaneFiveSquareExtrude(
    viewer: Viewer,
  ): Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>> {
    return DebugTool.createFrontPlaneSquareExtrudeByInput(viewer, {
      squareSide: DebugSquarePresetValue.QuickSquareSide,
      extrudeDepth: DEBUG_QUICK_EXTRUDE_DEPTH,
    });
  }

  /**
   * 使用前视基准面按指定尺寸创建正方形，并执行凸台拉伸。
   *
   * @param viewer Viewer 实例。
   * @param input 调试矩形拉伸输入参数。
   * @returns 拉伸提交结果。
   */
  public static async createFrontPlaneSquareExtrudeByInput(
    viewer: Viewer,
    input: DebugSquareExtrudeInput,
  ): Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>> {
    return DebugTool.executeFrontPlaneBossExtrude(
      viewer,
      () => {
        const rectangleCreationResult = DebugTool.createRectangleSketch(viewer, input.squareSide);
        return {
          sketchId: rectangleCreationResult.sketchId,
          contourOwnerIds: [rectangleCreationResult.contourOwnerId],
        };
      },
      input.extrudeDepth,
    );
  }

  //------ DebugTool 矩形圆形联合拉伸 开始------
  /**
   * 使用前视基准面创建 `10x10` 矩形与 `R5` 圆，并执行深度为 `10` 的多轮廓拉伸。
   *
   * @param viewer Viewer 实例。
   * @returns 拉伸提交结果。
   */
  public static async createFrontPlaneRectangleCircleExtrude(
    viewer: Viewer,
  ): Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>> {
    return DebugTool.createFrontPlaneRectangleCircleExtrudeByInput(viewer, {
      rectangleWidth: DEBUG_COMPOSITE_RECTANGLE_WIDTH,
      rectangleHeight: DEBUG_COMPOSITE_RECTANGLE_HEIGHT,
      circleRadius: DEBUG_COMPOSITE_CIRCLE_RADIUS,
      shapeGap: DEBUG_COMPOSITE_SHAPE_GAP,
      extrudeDepth: DEBUG_COMPOSITE_EXTRUDE_DEPTH,
    });
  }

  /**
   * 使用前视基准面按预设参数创建矩形与圆，并执行多轮廓拉伸。
   *
   * @param viewer Viewer 实例。
   * @param input 调试矩形圆形联合拉伸输入参数。
   * @returns 拉伸提交结果。
   */
  private static async createFrontPlaneRectangleCircleExtrudeByInput(
    viewer: Viewer,
    input: DebugRectangleCircleExtrudeInput,
  ): Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>> {
    return DebugTool.executeFrontPlaneBossExtrude(
      viewer,
      () => DebugTool.createRectangleCircleSketch(viewer, input),
      input.extrudeDepth,
    );
  }
  //------ DebugTool 矩形圆形联合拉伸 结束------

  //------ DebugTool 线弧 U 形轮廓调试 开始------
  /**
   * 在前视基准面创建线弧 U 形闭合轮廓草图，并停留在草图编辑态。
   *
   * 设计说明：
   * 1. 该入口用于复现 Collection 下直线与圆弧混合闭合轮廓拖拽场景；
   * 2. 创建完成后不结束草图会话，便于立刻拖拽上色轮廓验证刚体平移；
   * 3. 若当前已有活动草图会话则直接报错，避免把调试场景写入用户正在编辑的草图。
   *
   * @param viewer Viewer 实例。
   * @returns 草图创建结果。
   */
  public static async createFrontPlaneLineArcUProfileSketch(
    viewer: Viewer,
  ): Promise<DebugRectangleSketchCreationResult> {
    await viewer.whenReady();

    if (viewer.getActiveSketchGroupId()) {
      throw new Error('当前存在活动草图会话，无法创建线弧 U 形轮廓调试场景。');
    }

    const frontPlaneId = DebugTool.resolveFrontDatumPlaneId(viewer);
    viewer.enterDatumPlaneView(frontPlaneId);

    if (!viewer.startSketchSession({planeId: frontPlaneId})) {
      if (viewer.getCurrentViewMode() === ViewMode.DATUM_PLANE) {
        viewer.exitDatumPlaneView();
      }
      throw new Error('前视基准面草图会话启动失败，无法创建线弧 U 形轮廓调试场景。');
    }

    const result = DebugTool.createLineArcUProfileSketch(viewer);
    viewer.requestImmediateRender();
    return result;
  }
  //------ DebugTool 线弧 U 形轮廓调试 结束------

  /**
   * 在前视基准面上执行一次统一的草图创建与凸台拉伸流程。
   *
   * @param viewer Viewer 实例。
   * @param createSketchContent 草图内容创建回调。
   * @param extrudeDepth 拉伸深度。
   * @returns 拉伸提交结果。
   */
  private static async executeFrontPlaneBossExtrude(
    viewer: Viewer,
    createSketchContent: () => DebugMultiContourSketchCreationResult | Promise<DebugMultiContourSketchCreationResult>,
    extrudeDepth: number,
  ): Promise<Awaited<ReturnType<Viewer['commitExtrudeFeature']>>> {
    // 等待 Viewer 完成异步初始化，避免在渲染链未就绪时读取管理器。
    await viewer.whenReady();

    // 调试命令不允许在已有活动草图会话上继续追加，避免污染用户当前编辑上下文。
    if (viewer.getActiveSketchGroupId()) {
      throw new Error('当前存在活动草图会话，无法执行 DebugTool 调试建模。');
    }

    // 先解析前视基准面并进入法向视图，使当前参考面与草图所在平面保持一致。
    const frontPlaneId = DebugTool.resolveFrontDatumPlaneId(viewer);
    viewer.enterDatumPlaneView(frontPlaneId);

    try {
      // 再基于前视基准面启动草图会话，并写入调试草图对象。
      if (!viewer.startSketchSession({planeId: frontPlaneId})) {
        throw new Error('前视基准面草图会话启动失败。');
      }
      const sketchCreationResult = await createSketchContent();

      // 结束草图会话并提交草图定义，确保后续拉伸读取的是正式草图结果。
      const sketchTerminationResult = await viewer.endSketchSession();
      if (
        !sketchTerminationResult.endedGroupId
        || sketchTerminationResult.endedGroupId !== sketchCreationResult.sketchId
        || !sketchTerminationResult.committed
      ) {
        throw new Error('调试草图提交失败，无法继续执行拉伸。');
      }

      const contourIds = sketchCreationResult.contourOwnerIds.map((contourOwnerId) => {
        const contourResolution = viewer.resolveBossExtrudeSelectionContours(
          contourOwnerId,
        );
        if (!Array.isArray(contourResolution) || contourResolution.length !== 1) {
          throw new Error('调试草图未解析出唯一闭合轮廓，无法执行拉伸。');
        }
        return contourResolution[0].id;
      });

      // 通过现有凸台拉伸定义工厂构建标准定义，避免宿主层自行拼接底层命令参数。
      const extrudeDefinition = ExtrudeFeatureEditDefinitionFactory.createDefault({
        featureKind: 'boss-extrude',
        operation: 'boss',
      });

      const commitResult = await viewer.commitExtrudeFeature({
        selection: {
          kind: 'contours',
          contourIds,
        },
        definition: {
          ...extrudeDefinition,
          direction1: {
            ...extrudeDefinition.direction1,
            depth: extrudeDepth,
            reverse: false,
          },
        },
      });

      // 调试建模属于直接走底层提交链的宿主入口，这里需要主动把结果网格同步回场景。
      if (commitResult.meshData) {
        await viewer.addResultMesh(commitResult.meshData, {
          prototype: commitResult.prototype,
        });
      } else {
        const geometryStateSummary = await viewer.getGeometryStateSummary();
        await viewer.syncFeatureMeshesFromState(geometryStateSummary);
      }

      return commitResult;
    } finally {
      // DebugTool 不应污染宿主的“当前激活基准面”上下文，否则后续基准轴两平面模式会错误吸收该平面。
      if (viewer.getCurrentViewMode() === ViewMode.DATUM_PLANE) {
        viewer.exitDatumPlaneView();
      }
    }
  }
  //------ DebugTool 控制台 5x5 快速调试 结束------

  //------ DebugTool 控制台挂载 开始------
  /**
   * 将调试工具对象挂载到 `window.DebugTool`。
   *
   * @param viewer Viewer 实例。
   */
  public static mountToWindow(viewer: Viewer): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.DebugTool = DebugTool.createWindowFacade(viewer);
  }

  /**
   * 从 `window.DebugTool` 卸载调试工具对象。
   */
  public static unmountFromWindow(): void {
    if (typeof window === 'undefined') {
      return;
    }
    delete window.DebugTool;
  }

  /**
   * 创建控制台调试对象。
   *
   * @param viewer Viewer 实例。
   * @returns 控制台调试对象。
   */
  private static createWindowFacade(viewer: Viewer): DebugToolWindowFacade {
    return {
      getViewer: () => viewer,
      createFrontPlaneSquareExtrude: () => DebugTool.createFrontPlaneSquareExtrude(viewer),
      createFrontPlaneFiveSquareExtrude: () => DebugTool.createFrontPlaneFiveSquareExtrude(viewer),
      createFrontPlaneSquareExtrudeBySize: (
        squareSide: number,
        extrudeDepth: number = squareSide,
      ) => DebugTool.createFrontPlaneSquareExtrudeByInput(viewer, {
        squareSide,
        extrudeDepth,
      }),
      createFrontPlaneRectangleCircleExtrude: () => DebugTool.createFrontPlaneRectangleCircleExtrude(viewer),
      createTwoDistancedSquareSketches: () => DebugTool.createTwoDistancedSquareSketches(viewer),
      //------ DebugTool 线弧 U 形轮廓调试 开始------
      createFrontPlaneLineArcUProfileSketch: () => DebugTool.createFrontPlaneLineArcUProfileSketch(viewer),
      //------ DebugTool 线弧 U 形轮廓调试 结束------
      createSceneTestBox: () => DebugTool.createSceneTestBox(viewer),
      showAllTrimIntersectionBoxes: () => DebugTool.showAllTrimIntersectionBoxes(viewer),
      clearTrimIntersectionBoxes: () => DebugTool.clearTrimIntersectionBoxes(viewer),
      //------ DebugTool 场景测试线 开始------
      createSceneDebugLine: (
        start?: THREE.Vector3,
        end?: THREE.Vector3,
        color?: number,
      ) => DebugTool.createSceneDebugLine(viewer, start, end, color),
      //------ DebugTool 场景测试线 结束------
      //------ DebugTool UUID定位 开始------
      focusModelByUuid: (uuid: string) => DebugTool.focusModelByUuid(viewer, uuid),
      //------ DebugTool UUID定位 结束------
    };
  }
  //------ DebugTool 控制台挂载 结束------

  //------ DebugTool UUID定位 开始------
  /**
   * 按对象 uuid（兼容对象名）定位到模型。
   *
   * 设计说明：
   * 1. 优先复用 `Viewer.findSceneObjectById(...)`，保持与业务侧一致的对象解析语义；
   * 2. 定位逻辑统一走 `CameraManager.focusOnObjectBounds(...)`，避免宿主层重复维护相机数学；
   * 3. 命中后主动请求一次渲染，确保控制台调用即时可见。
   *
   * @param viewer Viewer 实例。
   * @param uuid 目标对象 uuid（兼容对象名）。
   * @returns 定位命中的对象；未命中时返回 `null`。
   */
  public static focusModelByUuid(viewer: Viewer, uuid: string): THREE.Object3D | null {
    const normalizedUuid = uuid.trim();
    if (normalizedUuid.length === 0) {
      throw new Error('uuid 不能为空，无法定位模型。');
    }

    const targetObject = viewer.findSceneObjectById(normalizedUuid);
    DebugTool.removeExistingFocusedObjectBounds(viewer);
    if (!targetObject) {
      return null;
    }

    DebugTool.createFocusedObjectBounds(viewer, targetObject);
    viewer.cameraManager?.focusOnObjectBounds(targetObject);
    viewer.requestImmediateRender();
    return targetObject;
  }
  //------ DebugTool UUID定位 结束------

  //------ DebugTool UUID定位包围盒 开始------
  /**
   * 为当前定位对象创建包围盒线框。
   *
   * @param viewer Viewer 实例。
   * @param targetObject 目标对象。
   */
  private static createFocusedObjectBounds(viewer: Viewer, targetObject: THREE.Object3D): void {
    if (!viewer.scene) {
      return;
    }

    const targetBox = new THREE.Box3().setFromObject(targetObject);
    if (targetBox.isEmpty()) {
      return;
    }

    DebugTool.createDebugBox(
      viewer,
      targetBox,
      DebugFocusBoundingBoxPreset.Color,
      DebugFocusBoundingBoxName.FocusedObjectBounds,
      DebugFocusBoundingBoxPreset.RenderOrder,
    );
  }

  /**
   * 清理已有的 UUID 定位包围盒。
   *
   * @param viewer Viewer 实例。
   */
  private static removeExistingFocusedObjectBounds(viewer: Viewer): void {
    if (!viewer.scene) {
      return;
    }

    const existed = viewer.scene.getObjectByName(DebugFocusBoundingBoxName.FocusedObjectBounds);
    if (!existed) {
      return;
    }

    viewer.scene.remove(existed);
    if (existed instanceof THREE.Mesh) {
      existed.geometry.dispose();
      const material = existed.material;
      if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
      } else {
        material.dispose();
      }
    }
  }
  //------ DebugTool UUID定位包围盒 结束------

  //------ DebugTool 场景测试盒 开始------
  /**
   * 创建一个 `2 x 2 x 2` 的测试盒并添加到 Viewer 主场景。
   *
   * 设计说明：
   * 1. 该函数只用于调试相机、scene 与 layer 可见性，不进入正式建模历史；
   * 2. 重复调用前会清理同名旧测试盒，避免调试对象无限累积导致场景排查混乱；
   * 3. 测试盒通过 `ignore` 标记避开选择与辅助计算链路，保持调试对象边界清晰。
   *
   * @param viewer Viewer 实例。
   * @returns 已添加到主场景中的测试盒 Mesh。
   */
  public static createSceneTestBox(
    viewer: Viewer,
  ): THREE.Mesh<THREE.BoxGeometry, THREE.MeshBasicMaterial> {
    if (!viewer.scene) {
      throw new Error('Viewer 场景不可用，无法创建 2x2x2 场景测试盒。');
    }

    // 先清理同名旧测试盒，确保控制台反复调用时始终只保留一个明确目标。
    DebugTool.removeExistingSceneTestBox(viewer);

    // 创建固定 2x2x2 几何体，便于通过相机裁剪、layer 与包围盒直接验证可见性。
    const geometry = new THREE.BoxGeometry(
      DebugSceneTestBoxPreset.Size,
      DebugSceneTestBoxPreset.Size,
      DebugSceneTestBoxPreset.Size,
    );
    const material = new THREE.MeshBasicMaterial({
      color: DebugSceneTestBoxPreset.Color,
      transparent: true,
      opacity: 0.8,
    });

    const testBox = new THREE.Mesh(geometry, material);
    testBox.name = DebugSceneTestBoxName.SceneTestBox;
    testBox.position.set(0, 0, 0);
    testBox.renderOrder = DebugSceneTestBoxPreset.RenderOrder;
    (testBox as unknown as { ignore?: boolean }).ignore = true;

    // 直接添加到主场景，确保用于排查当前主相机是否能看到 scene layer 0 对象。
    viewer.scene.add(testBox);
    viewer.requestImmediateRender();

    return testBox;
  }

  /**
   * 计算当前 Trim 候选集合的全部交点，并在每个交点创建圆形调试精灵。
   *
   * @param viewer Viewer 实例。
   * @returns 本次创建的交点调试精灵数量。
   */
  public static showAllTrimIntersectionBoxes(viewer: Viewer): number {
    const trimController = viewer.sketchTrimController;
    if (!trimController) {
      throw new Error('当前 Viewer 尚未创建草图裁剪控制器，无法显示 Trim 交点调试精灵。');
    }

    return trimController.debugShowAllTrimIntersections();
  }

  /**
   * 清理当前 Trim 交点调试精灵。
   *
   * @param viewer Viewer 实例。
   */
  public static clearTrimIntersectionBoxes(viewer: Viewer): void {
    const trimController = viewer.sketchTrimController;
    if (!trimController) {
      throw new Error('当前 Viewer 尚未创建草图裁剪控制器，无法清理 Trim 交点调试精灵。');
    }

    trimController.debugClearAllTrimIntersectionBoxes();
  }

  /**
   * 清理已有的场景测试盒。
   *
   * @param viewer Viewer 实例。
   */
  private static removeExistingSceneTestBox(viewer: Viewer): void {
    const existed = viewer.scene.getObjectByName(DebugSceneTestBoxName.SceneTestBox);
    if (!existed) {
      return;
    }

    viewer.scene.remove(existed);
    if (existed instanceof THREE.Mesh) {
      existed.geometry.dispose();
      const material = existed.material;
      if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
      } else {
        material.dispose();
      }
    }
  }
  //------ DebugTool 场景测试盒 结束------

  //------ DebugTool 场景测试线 开始------
  /**
   * 创建一条测试线并添加到 Viewer 主场景。
   *
   * 设计说明：
   * 1. 该函数只用于调试世界坐标、scene 与相机可见性，不进入正式建模历史；
   * 2. 重复调用前会清理同名旧测试线，避免控制台调试对象无限累积；
   * 3. 测试线通过 `ignore` 与 `skipSelection` 标记避开选择链路，保持调试对象边界清晰。
   *
   * @param viewer Viewer 实例。
   * @param start 测试线起点；缺省时使用 `(-50, 0, 0)`。
   * @param end 测试线终点；缺省时使用 `(50, 0, 0)`。
   * @param color 测试线颜色；缺省时使用内置调试蓝。
   * @returns 已添加到主场景中的测试线。
   */
  public static createSceneDebugLine(
    viewer: Viewer,
    start: THREE.Vector3 = DEBUG_SCENE_TEST_LINE_DEFAULT_START,
    end: THREE.Vector3 = DEBUG_SCENE_TEST_LINE_DEFAULT_END,
    color: number = DebugSceneTestLinePreset.Color,
  ): THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial> {
    if (!viewer.scene) {
      throw new Error('Viewer 场景不可用，无法创建场景测试线。');
    }

    DebugTool.removeExistingSceneDebugLine(viewer);

    const geometry = new THREE.BufferGeometry().setFromPoints([
      start.clone(),
      end.clone(),
    ]);
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: 1,
      depthTest: false,
      depthWrite: false,
    });
    const line = new THREE.Line(geometry, material);
    line.name = DebugSceneTestLineName.SceneTestLine;
    line.renderOrder = DebugSceneTestLinePreset.RenderOrder;
    line.userData.skipSelection = true;
    (line as unknown as { ignore?: boolean }).ignore = true;
    line.raycast = (): void => {};

    viewer.scene.add(line);
    viewer.requestImmediateRender();

    return line;
  }

  /**
   * 清理已有的场景测试线。
   *
   * @param viewer Viewer 实例。
   */
  private static removeExistingSceneDebugLine(viewer: Viewer): void {
    const existed = viewer.scene.getObjectByName(DebugSceneTestLineName.SceneTestLine);
    if (!existed) {
      return;
    }

    viewer.scene.remove(existed);
    if (existed instanceof THREE.Line) {
      existed.geometry.dispose();
      const material = existed.material;
      if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
      } else {
        material.dispose();
      }
    }
  }
  //------ DebugTool 场景测试线 结束------

  /**
   * 使用前视基准面创建两个相距一定间距的 50x50 正方形草图并执行拉伸。
   *
   * 设计说明：
   * 1. 第一个草图以草图原点为左下角；
   * 2. 第二个草图在 X 方向偏移 (SquareSide + BoxGap)，两者之间保持 BoxGap 距离；
   * 3. 两个草图对应两次独立草图会话，并在每次草图提交后执行一次凸台拉伸；
   * 4. 拉伸网格统一在循环后同步，避免迭代内重复同步导致场景重复添加。
   *
   * @param viewer Viewer 实例。
   */
  public static async createTwoDistancedSquareSketches(viewer: Viewer): Promise<void> {
    await viewer.whenReady();

    if (viewer.getActiveSketchGroupId()) {
      throw new Error('当前存在活动草图会话，无法执行 DebugTool 双草图调试建模。');
    }

    const frontPlaneId = DebugTool.resolveFrontDatumPlaneId(viewer);

    // 两个正方形在 X 方向上的起始偏移量列表，间距为 BoxGap。
    const xOffsets = [0, DebugSquarePresetValue.SquareSide + DebugSquarePresetValue.BoxGap] as const;

    // 统一暂存两次拉伸结果，循环结束后再统一做网格同步。
    const commitResults: Awaited<ReturnType<Viewer['commitExtrudeFeature']>>[] = [];
    let previousSketchId: string | null = null;

    for (const xOffset of xOffsets) {
      viewer.enterDatumPlaneView(frontPlaneId);

      try {
        //------ DebugTool 双草图会话隔离 开始------
        // 每次创建前先强制收口潜在残留会话，避免第二次误复用上一次草图组。
        await DebugTool.ensureNoActiveSketchSession(viewer, `xOffset=${xOffset}`);
        //------ DebugTool 双草图会话隔离 结束------

        if (!viewer.startSketchSession({ planeId: frontPlaneId })) {
          throw new Error(`前视基准面草图会话启动失败（xOffset=${xOffset}）。`);
        }

        const startedSketchId = viewer.getActiveSketchGroupId();
        if (!startedSketchId) {
          throw new Error(`调试矩形草图会话缺少活动草图组（xOffset=${xOffset}）。`);
        }
        if (previousSketchId && previousSketchId === startedSketchId) {
          throw new Error(`调试矩形草图会话未隔离，检测到草图组复用（xOffset=${xOffset}）。`);
        }

        const rectangleCreationResult = DebugTool.createRectangleSketchWithOffset(
          viewer,
          xOffset,
          0,
        );

        const sketchTerminationResult = await viewer.endSketchSession();
        if (
          !sketchTerminationResult.endedGroupId
          || sketchTerminationResult.endedGroupId !== rectangleCreationResult.sketchId
          || !sketchTerminationResult.committed
        ) {
          throw new Error(`调试矩形草图（xOffset=${xOffset}）提交失败。`);
        }

        const contourResolution = viewer.resolveBossExtrudeSelectionContours(
          rectangleCreationResult.contourOwnerId,
        );
        if (!Array.isArray(contourResolution) || contourResolution.length === 0) {
          throw new Error(`调试矩形草图（xOffset=${xOffset}）未解析出唯一闭合轮廓。`);
        }

        //------ DebugTool 轮廓归属隔离 开始------
        // 仅允许拉伸当前矩形集合创建出的轮廓，避免第二次提交误吸收其他草图轮廓。
        const ownedContours = contourResolution.filter(
          (contourItem) => contourItem.sourceObjectId === rectangleCreationResult.contourOwnerId,
        );
        if (ownedContours.length !== 1) {
          throw new Error(
            `调试矩形草图（xOffset=${xOffset}）轮廓归属异常，命中 ${ownedContours.length} 个当前矩形轮廓。`,
          );
        }
        //------ DebugTool 轮廓归属隔离 结束------

        const extrudeDefinition = ExtrudeFeatureEditDefinitionFactory.createDefault({
          featureKind: 'boss-extrude',
          operation: 'boss',
        });

        const commitResult = await viewer.commitExtrudeFeature({
          selection: {
            kind: 'contours',
            contourIds: [ownedContours[0].id],
          },
          definition: {
            ...extrudeDefinition,
            // 双草图调试期望生成两个独立实体，禁止第二次把第一次实体并入当前结果。
            mergeResult: false,
            direction1: {
              ...extrudeDefinition.direction1,
              depth: DEBUG_EXTRUDE_DEPTH,
              reverse: false,
            },
          },
        });

        commitResults.push(commitResult);
        previousSketchId = sketchTerminationResult.endedGroupId;
      } finally {
        if (viewer.getCurrentViewMode() === ViewMode.DATUM_PLANE) {
          viewer.exitDatumPlaneView();
        }
      }
    }

    const allHaveMeshData = commitResults.every((resultItem) => !!resultItem.meshData);
    if (allHaveMeshData) {
      for (const commitResult of commitResults) {
        await viewer.addResultMesh(commitResult.meshData!, {
          prototype: commitResult.prototype,
        });
      }
      return;
    }

    const geometryStateSummary = await viewer.getGeometryStateSummary();
    await viewer.syncFeatureMeshesFromState(geometryStateSummary);
  }

  /**
   * 确保当前不存在活动草图会话。
   *
   * 设计说明：
   * 1. `startSketchSession` 在已有活动草图组时会直接复用草图组；
   * 2. DebugTool 双草图场景必须强制“一次循环一个新草图组”；
   * 3. 这里统一做会话收口，避免调用点重复拼装清理逻辑。
   *
   * @param viewer Viewer 实例。
   * @param context 出错上下文。
   */
  private static async ensureNoActiveSketchSession(
    viewer: Viewer,
    context: string,
  ): Promise<void> {
    const activeSketchGroupId = viewer.getActiveSketchGroupId();
    if (!activeSketchGroupId) {
      return;
    }
    const terminationResult = await viewer.endSketchSession();
    if (!terminationResult.endedGroupId || terminationResult.endedGroupId !== activeSketchGroupId) {
      throw new Error(`收口残留草图会话失败（${context}）。`);
    }
  }

  /**
   * 解析前视基准面 ID。
   *
   * @param viewer Viewer 实例。
   * @returns 前视基准面 ID。
   */
  private static resolveFrontDatumPlaneId(viewer: Viewer): number {
    // 统一优先从 Viewer 的基准面摘要出口读取，并兼容旧运行时仍只暴露 `getAllDatumPlanes` 的情况。
    const planeSummaries = typeof (viewer as unknown as { listDatumPlaneSummaries?: unknown }).listDatumPlaneSummaries === 'function'
      ? viewer.listDatumPlaneSummaries()
      : viewer.getAllDatumPlanes();
    const frontPlane = planeSummaries.find((plane) => plane.name === DebugDatumPlaneName.Front);
    if (!frontPlane) {
      throw new Error('未找到前视基准面。');
    }
    return frontPlane.id;
  }

  /**
   * 创建调试矩形草图（带 X/Y 局部坐标偏移支持）。
   *
   * @param viewer Viewer 实例。
   * @param xOffset 草图局部坐标系下 X 方向偏移量（默认 0）。
   * @param yOffset 草图局部坐标系下 Y 方向偏移量（默认 0）。
   * @returns 草图创建结果。
   */
  private static createRectangleSketchWithOffset(
    viewer: Viewer,
    xOffset: number,
    yOffset: number,
  ): DebugRectangleSketchCreationResult {
    const referencePlane = viewer.currentReferencePlane;
    if (!referencePlane) {
      throw new Error('当前未设置草图参考面，无法创建调试矩形。');
    }

    const sketchId = viewer.getActiveSketchGroupId();
    if (!sketchId) {
      throw new Error('当前不存在活动草图组，无法创建调试矩形。');
    }

    const s = DebugSquarePresetValue.SquareSide;
    // 以偏移量为左下角，创建 s×s 正方形四个角点（局部坐标）。
    const cornersWorld = [
      referencePlane.localToWorld(xOffset, yOffset),
      referencePlane.localToWorld(xOffset + s, yOffset),
      referencePlane.localToWorld(xOffset + s, yOffset + s),
      referencePlane.localToWorld(xOffset, yOffset + s),
    ];

    const cornerPoints = cornersWorld.map((cornerWorld) =>
      DebugTool.createRectangleCornerPoint(referencePlane, cornerWorld),
    );
    const segmentLines = DebugTool.createRectangleSegmentLines(referencePlane, cornerPoints);
    DebugTool.applyDefaultRectangleRelations(segmentLines);

    const rectangleCollection = new SketchCollectionObject({
      sources: segmentLines,
      referencePlane,
    });
    if (viewer.shadedSketchContoursEnabled) {
      rectangleCollection.syncContour();
    }

    cornerPoints.forEach((cornerPoint) => {
      if (cornerPoint.parent !== rectangleCollection) {
        rectangleCollection.add(cornerPoint);
      }
    });

    segmentLines.forEach((segmentLine) => {
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(segmentLine);
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(segmentLine.startPoint);
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(segmentLine.endPoint);
    });

    viewer.rectangleSegmentGeometryMap.set(rectangleCollection.uuid, rectangleCollection);
    viewer.addSketchObject(rectangleCollection);
    viewer.notifySketchEntityCreated(rectangleCollection);

    const activeSketchGroup = viewer.sketchGroupManager?.activeSketchGroup ?? null;
    if (!activeSketchGroup) {
      throw new Error('活动草图组不存在，无法同步调试矩形轮廓显示状态。');
    }
    viewer.syncShadedContoursAfterSketchEntityCreated(activeSketchGroup);
    activeSketchGroup.markDefinitionDirty({
      reason: 'DebugTool 创建调试矩形草图（带偏移）',
      sourceObjectId: rectangleCollection.uuid,
    });

    return { sketchId, contourOwnerId: rectangleCollection.uuid };
  }

  /**
   * 创建调试矩形草图。
   *
   * @param viewer Viewer 实例。
   * @param squareSide 正方形边长。
   * @returns 草图创建结果。
   */
  private static createRectangleSketch(
    viewer: Viewer,
    squareSide: number,
  ): DebugRectangleSketchCreationResult {
    return DebugTool.createRectangleSketchBySize(viewer, {
      width: squareSide,
      height: squareSide,
      originLocal: new THREE.Vector2(0, 0),
      dirtyReason: 'DebugTool 创建调试矩形草图',
    });
  }

  //------ DebugTool 矩形圆形联合拉伸 开始------
  /**
   * 创建调试用的“矩形 + 圆”组合草图。
   *
   * @param viewer Viewer 实例。
   * @param input 调试矩形圆形联合拉伸输入参数。
   * @returns 多轮廓草图创建结果。
   */
  private static createRectangleCircleSketch(
    viewer: Viewer,
    input: DebugRectangleCircleExtrudeInput,
  ): DebugMultiContourSketchCreationResult {
    const rectangleCreationResult = DebugTool.createRectangleSketchBySize(viewer, {
      width: input.rectangleWidth,
      height: input.rectangleHeight,
      originLocal: new THREE.Vector2(0, 0),
      dirtyReason: 'DebugTool 创建调试矩形草图',
    });

    const circleCenterLocal = new THREE.Vector2(
      input.rectangleWidth + input.shapeGap + input.circleRadius,
      input.rectangleHeight * 0.5,
    );
    const circleCreationResult = DebugTool.createCircleSketch(viewer, {
      centerLocal: circleCenterLocal,
      radius: input.circleRadius,
      dirtyReason: 'DebugTool 创建调试圆草图',
    });

    return {
      sketchId: rectangleCreationResult.sketchId,
      contourOwnerIds: [rectangleCreationResult.contourOwnerId, circleCreationResult.contourOwnerId],
    };
  }
  //------ DebugTool 矩形圆形联合拉伸 结束------

  //------ DebugTool 线弧 U 形轮廓调试 开始------
  /**
   * 创建线弧 U 形闭合轮廓草图。
   *
   * 设计说明：
   * 1. 该轮廓按真实闭合集合创建，来源顺序与轮廓边界一致；
   * 2. 直线与圆弧共享端点，确保拖拽时复现 Collection 内线弧混合拓扑；
   * 3. 顶部横线与竖线挂载基础水平/竖直约束，便于观察拖拽后约束与几何是否同时保持稳定。
   *
   * @param viewer Viewer 实例。
   * @returns 草图创建结果。
   */
  private static createLineArcUProfileSketch(viewer: Viewer): DebugRectangleSketchCreationResult {
    const referencePlane = viewer.currentReferencePlane;
    if (!referencePlane) {
      throw new Error('当前未设置草图参考面，无法创建线弧 U 形轮廓。');
    }

    const sketchId = viewer.getActiveSketchGroupId();
    if (!sketchId) {
      throw new Error('当前不存在活动草图组，无法创建线弧 U 形轮廓。');
    }

    const outerRadius = DebugLineArcUProfilePreset.OuterRadius;
    const innerRadius = DebugLineArcUProfilePreset.InnerRadius;
    const innerCenterY = DebugLineArcUProfilePreset.InnerCenterY;
    const topY = DebugLineArcUProfilePreset.TopY;

    const leftOuterTop = DebugTool.createDebugSketchControlPoint(referencePlane, referencePlane.localToWorld(-outerRadius, topY));
    const leftOuterArc = DebugTool.createDebugSketchControlPoint(referencePlane, referencePlane.localToWorld(-outerRadius, 0));
    const rightOuterArc = DebugTool.createDebugSketchControlPoint(referencePlane, referencePlane.localToWorld(outerRadius, 0));
    const rightOuterTop = DebugTool.createDebugSketchControlPoint(referencePlane, referencePlane.localToWorld(outerRadius, topY));
    const rightInnerTop = DebugTool.createDebugSketchControlPoint(referencePlane, referencePlane.localToWorld(innerRadius, topY));
    const rightInnerArc = DebugTool.createDebugSketchControlPoint(referencePlane, referencePlane.localToWorld(innerRadius, innerCenterY));
    const leftInnerArc = DebugTool.createDebugSketchControlPoint(referencePlane, referencePlane.localToWorld(-innerRadius, innerCenterY));
    const leftInnerTop = DebugTool.createDebugSketchControlPoint(referencePlane, referencePlane.localToWorld(-innerRadius, topY));

    const leftOuterVertical = DebugTool.createDebugSketchLine(referencePlane, leftOuterTop, leftOuterArc);
    const outerArc = DebugTool.createDebugSketchArc(
      referencePlane,
      leftOuterArc,
      rightOuterArc,
      new THREE.Vector2(0, 0),
      outerRadius,
      Math.PI,
      0,
      false,
    );
    const rightOuterVertical = DebugTool.createDebugSketchLine(referencePlane, rightOuterArc, rightOuterTop);
    const rightTopHorizontal = DebugTool.createDebugSketchLine(referencePlane, rightOuterTop, rightInnerTop);
    const rightInnerVertical = DebugTool.createDebugSketchLine(referencePlane, rightInnerTop, rightInnerArc);
    const innerArc = DebugTool.createDebugSketchArc(
      referencePlane,
      rightInnerArc,
      leftInnerArc,
      new THREE.Vector2(0, innerCenterY),
      innerRadius,
      0,
      Math.PI,
      true,
    );
    const leftInnerVertical = DebugTool.createDebugSketchLine(referencePlane, leftInnerArc, leftInnerTop);
    const leftTopHorizontal = DebugTool.createDebugSketchLine(referencePlane, leftInnerTop, leftOuterTop);

    DebugTool.applyLineArcUProfileRelations({
      horizontalLines: [rightTopHorizontal, leftTopHorizontal],
      verticalLines: [leftOuterVertical, rightOuterVertical, rightInnerVertical, leftInnerVertical],
    });

    const boundaryPoints = [
      leftOuterTop,
      leftOuterArc,
      rightOuterArc,
      rightOuterTop,
      rightInnerTop,
      rightInnerArc,
      leftInnerArc,
      leftInnerTop,
    ];
    const lineSegments = [
      leftOuterVertical,
      rightOuterVertical,
      rightTopHorizontal,
      rightInnerVertical,
      leftInnerVertical,
      leftTopHorizontal,
    ];
    const arcs = [outerArc, innerArc];
    const collection = new SketchCollectionObject({
      sources: [
        leftOuterVertical,
        outerArc,
        rightOuterVertical,
        rightTopHorizontal,
        rightInnerVertical,
        innerArc,
        leftInnerVertical,
        leftTopHorizontal,
      ],
      referencePlane,
    });
    collection.name = DebugLineArcUProfileName.Collection;

    if (viewer.shadedSketchContoursEnabled) {
      collection.syncContour();
    }

    boundaryPoints.forEach((point) => {
      if (point.parent !== collection) {
        collection.add(point);
      }
    });

    lineSegments.forEach((line) => {
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(line);
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(line.startPoint);
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(line.endPoint);
    });
    arcs.forEach((arc) => {
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(arc);
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(arc.getCenterPoint());
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(arc.getStartPoint());
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(arc.getEndPoint());
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(arc.getMidPoint());
    });

    viewer.addSketchObject(collection);
    viewer.notifySketchEntityCreated(collection);

    const activeSketchGroup = viewer.sketchGroupManager?.activeSketchGroup ?? null;
    if (!activeSketchGroup) {
      throw new Error('活动草图组不存在，无法同步线弧 U 形轮廓显示状态。');
    }
    viewer.syncShadedContoursAfterSketchEntityCreated(activeSketchGroup);
    activeSketchGroup.markDefinitionDirty({
      reason: 'DebugTool 创建线弧 U 形轮廓调试草图',
      sourceObjectId: collection.uuid,
    });

    return {
      sketchId,
      contourOwnerId: collection.uuid,
    };
  }
  //------ DebugTool 线弧 U 形轮廓调试 结束------

  /**
   * 按指定尺寸创建矩形草图。
   *
   * @param viewer Viewer 实例。
   * @param input 矩形草图输入参数。
   * @returns 草图创建结果。
   */
  private static createRectangleSketchBySize(
    viewer: Viewer,
    input: {
      width: number;
      height: number;
      originLocal: THREE.Vector2;
      dirtyReason: string;
    },
  ): DebugRectangleSketchCreationResult {
    // 当前参考面必须由前面的法向视图入口统一建立，这里只读取，不做额外猜测。
    const referencePlane = viewer.currentReferencePlane;
    if (!referencePlane) {
      throw new Error('当前未设置草图参考面，无法创建调试矩形。');
    }

    const sketchId = viewer.getActiveSketchGroupId();
    if (!sketchId) {
      throw new Error('当前不存在活动草图组，无法创建调试矩形。');
    }

    // 固定以草图原点为左下角创建正方形，便于调试时直接观察局部坐标。
    const cornersWorld = [
      referencePlane.localToWorld(input.originLocal.x, input.originLocal.y),
      referencePlane.localToWorld(input.originLocal.x + input.width, input.originLocal.y),
      referencePlane.localToWorld(input.originLocal.x + input.width, input.originLocal.y + input.height),
      referencePlane.localToWorld(input.originLocal.x, input.originLocal.y + input.height),
    ];

    // 先创建共享控制点，再创建共享边段，保证矩形拓扑与现有矩形工具结果一致。
    const cornerPoints = cornersWorld.map((cornerWorld) => DebugTool.createRectangleCornerPoint(referencePlane, cornerWorld));
    const segmentLines = DebugTool.createRectangleSegmentLines(referencePlane, cornerPoints);

    // 为四条边挂载水平/竖直关系，使矩形保持标准正交约束语义。
    DebugTool.applyDefaultRectangleRelations(segmentLines);

    // 使用闭合集合对象承接矩形边段，并生成轮廓，供后续拉伸直接解析。
    const rectangleCollection = new SketchCollectionObject({
      sources: segmentLines,
      referencePlane,
    });
    if (viewer.shadedSketchContoursEnabled) {
      rectangleCollection.syncContour();
    }

    // 角点对象需要显式挂到集合下，保持与现有矩形绘制器一致的宿主结构。
    cornerPoints.forEach((cornerPoint) => {
      if (cornerPoint.parent !== rectangleCollection) {
        rectangleCollection.add(cornerPoint);
      }
    });

    // 将材质快照同步到交互状态管理器，避免后续选择态回退到错误材质。
    segmentLines.forEach((segmentLine) => {
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(segmentLine);
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(segmentLine.startPoint);
      viewer.interactionStateManager?.syncExternalMaterialSnapshot(segmentLine.endPoint);
    });

    // 记录矩形集合引用并添加到活动草图组，保持与现有矩形工具的注册链一致。
    viewer.rectangleSegmentGeometryMap.set(rectangleCollection.uuid, rectangleCollection);
    viewer.addSketchObject(rectangleCollection);
    viewer.notifySketchEntityCreated(rectangleCollection);

    // 草图轮廓显示状态需要在实体进入草图组后再次统一同步。
    const activeSketchGroup = viewer.sketchGroupManager?.activeSketchGroup ?? null;
    if (!activeSketchGroup) {
      throw new Error('活动草图组不存在，无法同步调试矩形轮廓显示状态。');
    }
    viewer.syncShadedContoursAfterSketchEntityCreated(activeSketchGroup);

    // 直接构造草图对象不会天然经过现有绘制命令的 dirty 上报链，
    // 这里需要显式标记草图定义已变更，确保 endSketchSession 会触发正式提交。
    activeSketchGroup.markDefinitionDirty({
      reason: input.dirtyReason,
      sourceObjectId: rectangleCollection.uuid,
    });

    return {
      sketchId,
      contourOwnerId: rectangleCollection.uuid,
    };
  }

  /**
   * 创建调试圆草图。
   *
   * @param viewer Viewer 实例。
   * @param input 圆草图输入参数。
   * @returns 草图创建结果。
   */
  private static createCircleSketch(
    viewer: Viewer,
    input: {
      centerLocal: THREE.Vector2;
      radius: number;
      dirtyReason: string;
    },
  ): DebugRectangleSketchCreationResult {
    const referencePlane = viewer.currentReferencePlane;
    if (!referencePlane) {
      throw new Error('当前未设置草图参考面，无法创建调试圆。');
    }

    const sketchId = viewer.getActiveSketchGroupId();
    if (!sketchId) {
      throw new Error('当前不存在活动草图组，无法创建调试圆。');
    }

    const lineMaterial = LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default);
    const circle = new SketchCircleObject({
      center: referencePlane.localToWorld(input.centerLocal),
      radius: input.radius,
      material: lineMaterial.clone(),
      referencePlane,
      isConstruction: false,
    });
    circle.updateGeometry();
    if (viewer.shadedSketchContoursEnabled) {
      circle.syncContour();
    }

    viewer.interactionStateManager?.syncExternalMaterialSnapshot(circle);
    viewer.interactionStateManager?.syncExternalMaterialSnapshot(circle.getCenterControlPoint());
    viewer.interactionStateManager?.syncExternalMaterialSnapshot(circle.getEdgeControlPoint());
    viewer.addSketchObject(circle);
    viewer.notifySketchEntityCreated(circle);

    const activeSketchGroup = viewer.sketchGroupManager?.activeSketchGroup ?? null;
    if (!activeSketchGroup) {
      throw new Error('活动草图组不存在，无法同步调试圆轮廓显示状态。');
    }
    viewer.syncShadedContoursAfterSketchEntityCreated(activeSketchGroup);
    activeSketchGroup.markDefinitionDirty({
      reason: input.dirtyReason,
      sourceObjectId: circle.uuid,
    });

    return {
      sketchId,
      contourOwnerId: circle.uuid,
    };
  }

  /**
   * 创建调试草图控制点。
   *
   * @param referencePlane 参考面。
   * @param worldPosition 点世界坐标。
   * @returns 草图控制点对象。
   */
  private static createDebugSketchControlPoint(
    referencePlane: Viewer['currentReferencePlane'],
    worldPosition: THREE.Vector3,
  ): SketchPointObject {
    if (!referencePlane) {
      throw new Error('参考面不存在，无法创建调试草图控制点。');
    }

    // 统一使用 cad-core 已有控制点材质工厂，保证视觉风格与交互语义一致。
    return new SketchPointObject({
      position: worldPosition.clone(),
      pointKind: SketchPointKind.ControlPoint,
      material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
      referencePlane,
    });
  }

  /**
   * 创建矩形角点控制点。
   *
   * @param referencePlane 参考面。
   * @param cornerWorld 角点世界坐标。
   * @returns 草图角点对象。
   */
  private static createRectangleCornerPoint(
    referencePlane: Viewer['currentReferencePlane'],
    cornerWorld: THREE.Vector3,
  ): SketchPointObject {
    return DebugTool.createDebugSketchControlPoint(referencePlane, cornerWorld);
  }

  //------ DebugTool 线弧 U 形轮廓调试 开始------
  /**
   * 创建调试草图线段。
   *
   * @param referencePlane 参考面。
   * @param startPoint 起点。
   * @param endPoint 终点。
   * @returns 草图线段对象。
   */
  private static createDebugSketchLine(
    referencePlane: Viewer['currentReferencePlane'],
    startPoint: SketchPointObject,
    endPoint: SketchPointObject,
  ): SketchLineObject {
    if (!referencePlane) {
      throw new Error('参考面不存在，无法创建调试草图线段。');
    }

    const lineMaterial = LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default);
    return new SketchLineObject({
      startPoint,
      endPoint,
      material: lineMaterial.clone(),
      referencePlane,
    });
  }

  /**
   * 创建调试草图圆弧。
   *
   * @param referencePlane 参考面。
   * @param startPoint 圆弧起点。
   * @param endPoint 圆弧终点。
   * @param centerLocal 圆心局部坐标。
   * @param radius 圆弧半径。
   * @param startAngle 起始角。
   * @param endAngle 终止角。
   * @param clockwise 是否顺时针。
   * @returns 草图圆弧对象。
   */
  private static createDebugSketchArc(
    referencePlane: Viewer['currentReferencePlane'],
    startPoint: SketchPointObject,
    endPoint: SketchPointObject,
    centerLocal: THREE.Vector2,
    radius: number,
    startAngle: number,
    endAngle: number,
    clockwise: boolean,
  ): SketchArcObject {
    if (!referencePlane) {
      throw new Error('参考面不存在，无法创建调试草图圆弧。');
    }

    const lineMaterial = LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default);
    const centerWorld = referencePlane.localToWorld(centerLocal);
    return new SketchArcObject({
      referencePlane,
      definition: {
        centerWorld,
        radius,
        startAngle,
        endAngle,
        clockwise,
        segments: DebugLineArcUProfilePreset.ArcSegments,
      },
      startPoint,
      endPoint,
      centerPoint: DebugTool.createDebugSketchControlPoint(referencePlane, centerWorld),
      material: lineMaterial.clone(),
    });
  }

  /**
   * 为线弧 U 形轮廓挂载基础水平/竖直约束。
   *
   * @param input 约束目标线段集合。
   */
  private static applyLineArcUProfileRelations(input: {
    /** 水平线段列表。 */
    horizontalLines: readonly SketchLineObject[];
    /** 竖直线段列表。 */
    verticalLines: readonly SketchLineObject[];
  }): void {
    input.horizontalLines.forEach((line) => {
      // 顶部两段必须保持水平，便于拖拽后观察 U 形开口宽度是否仍稳定。
      line.constraints.addRelation(new LineHorizontalConstraint());
    });
    input.verticalLines.forEach((line) => {
      // 四条竖边必须保持竖直，对齐截图中的基础几何关系。
      line.constraints.addRelation(new LineVerticalConstraint());
    });
  }
  //------ DebugTool 线弧 U 形轮廓调试 结束------

  /**
   * 创建矩形边段集合。
   *
   * @param referencePlane 参考面。
   * @param cornerPoints 角点控制点列表。
   * @returns 矩形四条边段。
   */
  private static createRectangleSegmentLines(
    referencePlane: Viewer['currentReferencePlane'],
    cornerPoints: readonly SketchPointObject[],
  ): SketchLineObject[] {
    if (!referencePlane) {
      throw new Error('参考面不存在，无法创建矩形边段。');
    }
    if (cornerPoints.length !== 4) {
      throw new Error('矩形角点数量不正确，无法创建矩形边段。');
    }

    // 统一复用 cad-core 线材质工厂，避免宿主层自行定义与草图默认样式不一致的线材质。
    const lineMaterial = LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default);

    return cornerPoints.map((cornerPoint, index) => {
      const nextPoint = cornerPoints[(index + 1) % cornerPoints.length]!;

      // 每条线都共享角点对象，这样拖拽端点时仍能保持标准矩形拓扑。
      return new SketchLineObject({
        startPoint: cornerPoint,
        endPoint: nextPoint,
        material: lineMaterial.clone(),
        referencePlane,
      });
    });
  }

  /**
   * 为矩形边段挂载默认约束。
   *
   * @param segmentLines 矩形边段列表。
   */
  private static applyDefaultRectangleRelations(
    segmentLines: readonly SketchLineObject[],
  ): void {
    if (segmentLines.length !== 4) {
      throw new Error('矩形边段数量不正确，无法挂载默认矩形约束。');
    }

    // 偶数边保持水平，奇数边保持竖直，对齐现有角点矩形的默认约束策略。
    segmentLines.forEach((segmentLine, index) => {
      const relationConstraint = index % 2 === 0
        ? new LineHorizontalConstraint()
        : new LineVerticalConstraint();
      segmentLine.constraints.addRelation(relationConstraint);
    });
  }

  /**
   * 创建碰撞检测调试用的两个包围盒。
   * 用于可视化碰撞检测时被拖拽对象和场景物体的包围盒范围。
   *
   * @param viewer Viewer 实例。
   * @param dragObjectBox - 被拖拽对象的包围盒，用蓝色表示。
   * @param sceneObjectBox - 场景物体的包围盒，用黄色表示。
   */
  public static createCollisionDebugBoxes(
    viewer: Viewer,
    dragObjectBox: THREE.Box3,
    sceneObjectBox: THREE.Box3,
  ): void {
    // 为被拖拽对象创建蓝色调试 Box。
    DebugTool.createDebugBox(
      viewer,
      dragObjectBox,
      0x3b82f6, // 蓝色
      'CollisionDebugBox_DragObject',
    );

    // 为场景物体创建黄色调试 Box。
    DebugTool.createDebugBox(
      viewer,
      sceneObjectBox,
      0xfbbf24, // 黄色
      'CollisionDebugBox_SceneObject',
    );
  }

  /**
   * 创建单个调试用的包围盒网格。
   * 用于可视化包围盒范围，便于调试和诊断。
   *
   * @param viewer Viewer 实例。
   * @param box - 包围盒。
   * @param color - 调试盒颜色（十六进制数值）。
   * @param name - 调试盒名称。
   */
  private static createDebugBox(
    viewer: Viewer,
    box: THREE.Box3,
    color: number,
    name: string,
    renderOrder: number = 998,
  ): void {
    if (!viewer.scene) {
      console.warn('[DebugTool] Viewer 场景不可用，无法创建调试 Box。');
      return;
    }

    // 计算包围盒的中心和尺寸。
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    // 创建立方体几何体和材质。
    const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.3,
      depthTest: false,
      depthWrite: false,
      wireframe: true,
    });

    // 创建网格对象并设置调试属性。
    const debugBox = new THREE.Mesh(geometry, material);
    (debugBox as any).ignore = true;
    debugBox.name = name;
    debugBox.renderOrder = renderOrder;
    debugBox.position.copy(center);

    // 添加到场景中。
    viewer.scene.add(debugBox);

    console.log(`[DebugTool] 创建调试 Box: ${name}，颜色: #${color.toString(16).padStart(6, '0')}`);
  }

  /**
   * 清理所有调试 Box。
   * 从场景中移除并释放所有调试用的包围盒资源。
   *
   * @param viewer Viewer 实例。
   */
  public static clearCollisionDebugBoxes(viewer: Viewer): void {
    if (!viewer.scene) {
      console.warn('[DebugTool] Viewer 场景不可用，无法清理调试 Box。');
      return;
    }

    // 查找并移除所有调试 Box。
    const debugBoxesToRemove: THREE.Object3D[] = [];
    viewer.scene.traverse((obj) => {
      if (obj.name.startsWith('CollisionDebugBox_')) {
        debugBoxesToRemove.push(obj);
      }
    });

    debugBoxesToRemove.forEach((debugBox) => {
      viewer.scene?.remove(debugBox);
      if (debugBox instanceof THREE.Mesh) {
        debugBox.geometry.dispose();
        (debugBox.material as THREE.Material).dispose();
      }
    });

    if (debugBoxesToRemove.length > 0) {
      console.log(`[DebugTool] 已清理 ${debugBoxesToRemove.length} 个调试 Box。`);
    }
  }
}
